import { createStore, filterNil, select, withProps } from '@ngneat/elf';
import { joinRequestResult } from '@ngneat/elf-requests';
import { CONTENT_TOOLS } from '@shared/core-utils';
import { InitializationState } from '@tmx/core';
import { trackRequest } from '@tmx/core/elf-utils';
import { AccountSummary, Userinfo, UserProduct } from '@tmx/data-access';
import { getPreferredTheme, Theme } from '@tmx/modus-operandi';
import { RowsDescriptor } from '@tmx/operandi';
import { distinctUntilChanged, map, shareReplay } from 'rxjs';

import { EMPTY_ROWS_DESCRIPTOR } from '../../models/empty-rows-descriptor';
import { MapsSdkRegion } from '../../models/maps-sdk-region';
import { PcmWsDataSet } from '../../models/pcmws-data-set';
import { PrimaryNavOptions } from '../../models/primary-nav-options';
import { Region } from '../../models/region';
import { requestCompletedAndSucceeded } from '../../utils/request-succeeded';

export const COPILOT_APPLICATION_ID = 21;
export const MILEON_APPLICATION_ID = 72;

const OMNI_ACCOUNT_ALIAS = 'B7SA29';

type RequestKeys =
  | 'check-has-assets'
  | 'load-account-summary'
  | 'load-webtool-items';

export const DEFAULT_REGION: Region = 'No Region';
export const DEFAULT_PCMWS_DATASET: PcmWsDataSet = 'PCM_WW';
export const DEFAULT_MAPS_SDK_REGION: MapsSdkRegion = 'na';

interface AmsProps {
  accountHasCoPilot: boolean;
  accountHasMileOn: boolean;
  accountSummary: AccountSummary | null;
  amsState: InitializationState;
  licenseProductRowsDescriptor: RowsDescriptor;
  mapsSdkRegion: MapsSdkRegion;
  onboardingShifted: boolean;
  pcmWsDataSet: PcmWsDataSet;
  primaryNavOptions: null | PrimaryNavOptions;
  region: Region;
  theme: Theme;
  unfilteredAssetSize: number;
  userInfo: null | Userinfo;
  webToolItems: null | UserProduct[];
}

const amsStore = createStore(
  { name: 'ams' },
  withProps<AmsProps>({
    amsState: 'loading',
    region: DEFAULT_REGION,
    pcmWsDataSet: DEFAULT_PCMWS_DATASET,
    mapsSdkRegion: DEFAULT_MAPS_SDK_REGION,
    unfilteredAssetSize: 0,
    accountSummary: null,
    webToolItems: null,
    primaryNavOptions: null,
    licenseProductRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    accountHasCoPilot: false,
    accountHasMileOn: false,
    userInfo: null,
    onboardingShifted: false,
    theme: getPreferredTheme(),
  })
);

/** Snapshots */

export const amsStoreSnapshot = () => amsStore.getValue();

export const accountSummarySnapshot = () => {
  const { accountSummary } = amsStoreSnapshot();
  if (accountSummary === null) {
    throw new Error(
      'accountSummarySnapshot function called before account summary initialized.'
    );
  }

  return accountSummary;
};

export const webToolItemsSnapshot = () => {
  const { webToolItems } = amsStoreSnapshot();
  if (webToolItems === null) {
    throw new Error(
      'webToolItemsSnapshot function called before web tool items initialized.'
    );
  }

  return webToolItems;
};

export const isNotOmniAccount = () => {
  const { partnerId } = accountSummarySnapshot();

  return partnerId !== OMNI_ACCOUNT_ALIAS;
};

export const canModifyAssetsBasedOnEldAndCompanyType = () => {
  const { companyType, eldPartnerType } = accountSummarySnapshot();

  if (companyType === 'Internal Test' || companyType === 'Partner') {
    return true;
  }

  if (
    eldPartnerType === 'No Integration' &&
    (companyType === 'Trial' || companyType === 'Paying Customer')
  ) {
    return true;
  }

  return false;
};

export const canModifyLicensesBasedOnEldAndCompanyType = () => {
  const { companyType, eldPartnerType } = accountSummarySnapshot();

  if (companyType === 'Internal Test' || companyType === 'Partner') {
    return true;
  }

  if (
    (eldPartnerType === 'No Integration' ||
      eldPartnerType === 'Common Partner Hub Integration') &&
    (companyType === 'Trial' || companyType === 'Paying Customer')
  ) {
    return true;
  }

  return false;
};

/** Request Results Trackers */

export const trackAmsRequests = <T>(keys: RequestKeys[]) =>
  trackRequest<T, RequestKeys>(keys);

export const joinAmsRequests = <T>(keys: RequestKeys[]) =>
  joinRequestResult<T>(keys);

export const checkedHasAssets$ = amsStore.pipe(
  select(({ unfilteredAssetSize }) => unfilteredAssetSize),
  joinAmsRequests(['check-has-assets']),
  requestCompletedAndSucceeded()
);

export const loadedAccountSummary$ = amsStore.pipe(
  select(({ accountSummary }) => accountSummary),
  joinAmsRequests(['load-account-summary']),
  requestCompletedAndSucceeded()
);

export const loadedWebToolItems$ = amsStore.pipe(
  select(({ webToolItems }) => webToolItems),
  joinAmsRequests(['load-webtool-items']),
  requestCompletedAndSucceeded()
);

/** Exposed Variables and Observables */

export const amsState$ = amsStore.pipe(select(({ amsState }) => amsState));

export const hasAssets$ = amsStore.pipe(
  select(({ unfilteredAssetSize }) => unfilteredAssetSize > 0)
);

export const accountSummary$ = amsStore.pipe(
  select(({ accountSummary }) => accountSummary),
  filterNil(),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const products$ = accountSummary$.pipe(
  map(({ products }) => products),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const licenseProductRowsDescriptor$ = amsStore.pipe(
  select((state) => state.licenseProductRowsDescriptor)
);

export const primaryNavOptionsInitialized$ = amsStore.pipe(
  select(({ primaryNavOptions }) => primaryNavOptions),
  map((primaryNavOptions) => primaryNavOptions !== null),
  distinctUntilChanged(),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const webToolItems$ = amsStore.pipe(
  select(({ webToolItems }) => webToolItems),
  filterNil(),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const primaryNavOptions$ = amsStore.pipe(
  select(({ primaryNavOptions }) => primaryNavOptions),
  filterNil()
);

export const userInfo$ = amsStore.pipe(
  select(({ userInfo }) => userInfo),
  filterNil()
);

export const theme$ = amsStore.pipe(select(({ theme }) => theme));

export const onboardingShifted$ = amsStore.pipe(
  select(({ onboardingShifted }) => onboardingShifted),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const licensedForContentTools$ = amsStore.pipe(
  select(({ webToolItems }) => webToolItems ?? []),
  map(
    (items) =>
      items.filter(
        (item) => item.name === CONTENT_TOOLS && item.licensed && !item.expired
      ).length > 0
  )
);

let amsRedirectUrl: null | string = null;

export function getAmsRedirectUrl() {
  return amsRedirectUrl;
}

export function setAmsRedirectUrl(value: string) {
  amsRedirectUrl = value;
}

export function clearAmsRedirectUrl() {
  amsRedirectUrl = null;
}

/** Updater Functions */

export function updateAmsState(amsState: InitializationState) {
  amsStore.update((state) => ({
    ...state,
    amsState,
  }));
}

export function updateRegion(region: Region) {
  amsStore.update((state) => ({
    ...state,
    region,
  }));
}

export function updatePcmWsDataSet(pcmWsDataSet: PcmWsDataSet) {
  amsStore.update((state) => ({
    ...state,
    pcmWsDataSet,
  }));
}

export function updateMapsSdkRegion(mapsSdkRegion: MapsSdkRegion) {
  amsStore.update((state) => ({
    ...state,
    mapsSdkRegion,
  }));
}

export function updateUnfilteredAssetSize(unfilteredAssetSize: number) {
  amsStore.update((state) => ({
    ...state,
    unfilteredAssetSize,
  }));
}

export function updateAccountSummary(accountSummary: AccountSummary) {
  let accountHasMileOn = false;
  let accountHasCoPilot = false;
  accountSummary.products.forEach(({ applicationId }) => {
    if (applicationId === COPILOT_APPLICATION_ID) {
      accountHasCoPilot = true;
    }
    if (applicationId === MILEON_APPLICATION_ID) {
      accountHasMileOn = true;
    }
  });
  amsStore.update((state) => ({
    ...state,
    accountSummary,
    accountHasCoPilot,
    accountHasMileOn,
  }));
}

export function updateWebToolItems(webToolItems: UserProduct[]) {
  amsStore.update((state) => ({
    ...state,
    webToolItems,
  }));
}

export function updateLicenseProductRowsDescriptor(
  licenseProductRowsDescriptor: RowsDescriptor
) {
  amsStore.update((state) => ({
    ...state,
    licenseProductRowsDescriptor,
  }));
}

export function updatePrimaryNavOptions(primaryNavOptions: PrimaryNavOptions) {
  amsStore.update((state) => ({
    ...state,
    primaryNavOptions,
  }));
}

export function switchAccountResetAmsStore() {
  amsStore.update((state) => ({
    ...state,
    amsState: 'loading',
    primaryNavOptions: null,
  }));
}

export function updateUserInfo(userInfo: Userinfo) {
  amsStore.update((state) => ({
    ...state,
    userInfo,
  }));
}

export function updateTheme(theme: Theme) {
  amsStore.update((state) => ({
    ...state,
    theme,
  }));
}

export function updateOnboardingShifted(onboardingShifted: boolean) {
  amsStore.update((state) => ({
    ...state,
    onboardingShifted,
  }));
}

export function resetAmsStore() {
  amsStore.reset();
}
